import { useState, useEffect } from 'react';

import { THERMAL_ZONE_STATUS } from '../../config/status';
import { CONTROL_MODE } from '../../config/control';
import { ACTIVITY } from '../../config/activity';
import { useCampusThermalZones } from '../../context/CampusContextProvider';

import { ThermalZoneStatusBlock } from '../../components/ThermalZoneStatus/ThermalZoneStatusBlock';
import { DataItem } from '../../components/DataItem/DataItem';
import { SectionCollapseable } from '../../components/SectionCollapseable/SectionCollapseable';

import './ComfortOverview.scss';

export const ComfortOverview = ({ campusData }) => {
  const campusThermalZones = useCampusThermalZones();
  const [activities, setActivities] = useState({});
  const [statuses, setStatuses] = useState({});
  const [controls, setControls] = useState({});

  useEffect(() => {
    updateOverviewData();
  }, [campusData, campusThermalZones]);

  const updateOverviewData = () => {
    setActivities({}); // reset
    setStatuses({}); // reset
    setControls({}); // reset

    campusData?.buildings?.forEach((building) => {
      const buildingActivities = building?.thermalZoneAnalysis?.activities;
      // const buildingStatuses = building?.thermalZoneAnalysis?.statuses;

      if (buildingActivities) {
        Object.keys(buildingActivities).forEach((activityKey) => {
          if (activities[activityKey]) {
            setActivities((prevActivities) => {
              return {
                ...prevActivities,
                [activityKey]: prevActivities[activityKey]
                  ? prevActivities[activityKey] + buildingActivities[activityKey]
                  : buildingActivities[activityKey],
              };
            });
          } else {
            setActivities((prevActivities) => ({
              ...prevActivities,
              [activityKey]: buildingActivities[activityKey],
            }));
          }
        });
      }
    });

    if (campusThermalZones) {
      campusThermalZones.forEach((zone) => {
        if (zone.activeControlStrategy) {
          const controlKey = zone.activeControlStrategy.mode.toUpperCase();

          setControls((prevControls) => ({
            ...prevControls,
            [controlKey]: prevControls[controlKey] ? prevControls[controlKey] + 1 : 1,
          }));
        }
        if (zone.status) {
          const statusKey = zone.status;
          setStatuses((prevStatuses) => ({
            ...prevStatuses,
            [statusKey.label]: prevStatuses[statusKey.label]
              ? prevStatuses[statusKey.label] + 1
              : 1,
          }));
        }
      });
    }
  };

  const renderActivities = () => {
    const activityOrder = Object.keys(ACTIVITY);
    const mappedActivities = () =>
      Object.keys(activities)
        .sort((a, b) => {
          const ai = activityOrder.indexOf(a.toUpperCase());
          const bi = activityOrder.indexOf(b.toUpperCase());
          return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
        })
        .map((activity) => {
          return (
            <div key={activity} className={`item activity-item`}>
              <div className="label">
                <DataItem label={activity} icon={ACTIVITY[activity.toUpperCase()].icon} />
              </div>
              <div className="count">{activities[activity]}</div>
            </div>
          );
        });
    return (
      <div className="activities-list">
        <div className={`item header`}>
          <div className="label">
            <span>Activity</span>
          </div>
          <div className="count">
            <span>Zones</span>
          </div>
        </div>
        {mappedActivities()}
      </div>
    );
  };

  const renderStatuses = () => {
    const statusOrder = Object.values(THERMAL_ZONE_STATUS).map((s) => s.label);
    const mappedStatuses = () =>
      Object.keys(statuses)
        .sort((a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b))
        .map((status) => {
          const statusMapped = Object.values(THERMAL_ZONE_STATUS).find((s) => s.label === status);

          return (
            <div key={status} className={`item status-item`}>
              <div className="label">
                <ThermalZoneStatusBlock status={statusMapped} />
              </div>
              <div className="count">{statuses[status]}</div>
            </div>
          );
        });
    return (
      <div className="statuses-list">
        <div className={`item header`}>
          <div className="label">
            <span>Status</span>
          </div>
          <div className="count">
            <span>Zones</span>
          </div>
        </div>
        {mappedStatuses()}
      </div>
    );
  };

  const renderControls = () => {
    const controlOrder = Object.keys(CONTROL_MODE);
    const mappedControls = Object.keys(controls)
      .sort((a, b) => {
        const ai = controlOrder.indexOf(a);
        const bi = controlOrder.indexOf(b);
        return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
      })
      .map((control) => {
        return (
          <div key={control} className={`item control-item`}>
            <div className="label">
              <DataItem label={CONTROL_MODE[control]?.label} icon={CONTROL_MODE[control]?.icon} />
            </div>
            <div className="count">{controls[control]}</div>
          </div>
        );
      });

    return (
      <div className="control-list">
        <div className={`item header`}>
          <div className="label">
            <span>Control</span>
          </div>
          <div className="count">
            <span>Zones</span>
          </div>
        </div>
        {mappedControls}
      </div>
    );
  };

  return (
    <div className="comfort-overview content-container">
      {/* <p>Total Thermal Zones: {campusThermalZones.length}</p> */}
      <SectionCollapseable heading="Summary">
        <div className="comfort-overview-content">
          <div className="content-block--inline">
            <h4>Zone Statuses</h4>
            <div className="listview zone-statuses--list">{renderStatuses()}</div>
          </div>
          <div className="content-block--inline">
            <h4>Active Controls</h4>
            <div className="listview active-controls--list">{renderControls()}</div>
          </div>
          <div className="content-block--inline">
            <h4>Activities</h4>
            <div className="listview activities--list">{renderActivities()}</div>
          </div>
        </div>
      </SectionCollapseable>
    </div>
  );
};
